// src/pages/CareersPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import SectionHeader from '../components/SectionHeader';
import SEO from '../components/SEO';
import { API_BASE_URL } from '../config';
import { stripHtml } from '../utils/html';
import '../styles/CareersPage.css';

interface Job {
  _id: string;
  title: string;
  location: string;
  employmentType: string;
  salaryRange?: string;
  description: string;
  applicationEmail: string;
  deadline?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  'full-time': 'Full-time',
  'part-time': 'Part-time',
  'contract': 'Contract',
  'internship': 'Internship',
  'volunteer': 'Volunteer',
};

const CareersPage: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/jobs`);

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const data = await response.json();
      // Only show published vacancies whose deadline (if any) has not passed
      const openJobs = data.filter((job: Job) =>
        job.status === 'published' &&
        (!job.deadline || new Date(job.deadline).getTime() >= Date.now())
      );

      setJobs(openJobs);
    } catch (err) {
      setError('Failed to load job vacancies. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const employmentTypes = Array.from(new Set(jobs.map(job => job.employmentType)));

  const filteredJobs = activeFilter === 'all'
    ? jobs
    : jobs.filter(job => job.employmentType === activeFilter);

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncate = (text: string, max = 160) =>
    text.length > max ? text.substring(0, max) + '...' : text;

  return (
    <>
      <SEO
        title="Careers & Job Vacancies"
        description="Join the EmpowerFuture21 team. Explore current job vacancies and opportunities to help empower women, youth, and communities."
        keywords="careers, jobs, vacancies, employment, opportunities, work with us, NGO jobs"
      />

      <Hero
        title="Careers at EmpowerFuture21"
        subtitle="Join us in empowering communities through education, skills, and sustainable development"
        smallHero={true}
      />

      <section className="careers-section">
        <div className="container">
          <SectionHeader
            title="Current Openings"
            subtitle="Explore our open positions and become part of our mission"
          />

          {loading ? (
            <div className="careers-loading">
              <div className="loading-spinner"></div>
              <p>Loading job vacancies...</p>
            </div>
          ) : error ? (
            <div className="careers-error">
              <p>{error}</p>
            </div>
          ) : (
            <>
              {employmentTypes.length > 0 && (
                <div className="careers-filters">
                  <button
                    className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('all')}
                  >
                    All
                  </button>
                  {employmentTypes.map(type => (
                    <button
                      key={type}
                      className={`filter-btn ${activeFilter === type ? 'active' : ''}`}
                      onClick={() => setActiveFilter(type)}
                    >
                      {EMPLOYMENT_TYPE_LABELS[type] || type}
                    </button>
                  ))}
                </div>
              )}

              {filteredJobs.length === 0 ? (
                <div className="no-jobs">
                  <p>There are no open positions at the moment. Please check back later.</p>
                </div>
              ) : (
                <div className="careers-list">
                  {filteredJobs.map(job => (
                    <div className="job-card" key={job._id}>
                      <div className="job-card-main">
                        <div className="job-card-header">
                          <h3 className="job-card-title">{job.title}</h3>
                          <span className="job-card-type">
                            {EMPLOYMENT_TYPE_LABELS[job.employmentType] || job.employmentType}
                          </span>
                        </div>

                        <div className="job-card-meta">
                          <span className="job-card-location">📍 {job.location}</span>
                          {job.salaryRange && <span className="job-card-salary">💰 {job.salaryRange}</span>}
                          {job.deadline && (
                            <span className="job-card-deadline">⏰ Apply by {formatDate(job.deadline)}</span>
                          )}
                        </div>

                        <p className="job-card-desc">{truncate(stripHtml(job.description))}</p>
                      </div>

                      <div className="job-card-actions">
                        <Link to={`/careers/${job._id}`} className="job-card-link">
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default CareersPage;
